import React from 'react'
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'
import PlaceComponent from './Place'
import TimeField from './TimeField'
import { MultiTextItemField } from './fields/MultiTextItemField'
import UserSelectFieldComponent from './UserSelectField'
import { MultiTextItemFieldArray } from './fields/MultiTextItemFieldArray'
import UserMultiSelectFieldComponent from './UserMultiSelectField'
import { TimeZoneField } from './fields/TimeZoneField'
import { MultiTextItemFieldPlusExtras } from './fields/MultiTextItemFieldPlusExtras'

export const RenderField = (
  field: any,
  key: any,
  onDataEntry: any,
  fieldValue: any = '',
  options: any = {},
  children: any = null,
  onCompositeFieldChange?: Function | any,
  setErr?: any
) => {
  switch (field.fieldType) {
    case 'number':
      return (
        <TextField
          id={field.name}
          name={field.name}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue}
          required={field.isRequired}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          disabled={field.disabled ? true : false}
        />
      )
    case 'valuelist':
      return (
        <FormControl sx={{ mt: 1, mb: 1, width: 300 }}>
          <InputLabel id={field.name}>{field.label}</InputLabel>
          <Select
            labelId={field.name}
            id={field.name}
            name={field.name}
            key={key}
            variant='filled'
            value={[]}
            multiple>
            {options &&
              options[field.name].map((option: any, key: any) => (
                <MenuItem key={key} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )
    case 'multiselect':
      return (
        <FormControl sx={{ mt: 1, mb: 1, width: 300 }}>
          <Select
            id={field.name}
            name={field.name}
            key={key}
            variant='filled'
            value={[]}
            multiple>
            {field.options.map((option: any, key: any) => (
              <MenuItem key={key} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    case 'singleselect':
      return (
        <FormControl
          sx={{ mt: 1, mb: 1, width: ['100%', '100%', '100%', 300] }}>
          <Select
            id={field.name}
            name={field.name}
            value={fieldValue}
            placeholder={field.placeholder}
            disabled={field.disabled}
            variant='filled'
            onChange={onDataEntry}>
            {field.options &&
              field.options.map((option: any, key: any) => (
                <MenuItem key={key} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )
    case 'timezone':
      return (
        <FormControl
          sx={{ mt: 1, mb: 1, width: ['100%', '100%', '100%', 300] }}>
          <TimeZoneField
            field={field}
            fieldValue={fieldValue}
            onDataEntry={onDataEntry}
          />
        </FormControl>
      )
    case 'date':
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label=''
            value={fieldValue}
            onChange={newValue => onDataEntry(newValue, field.name)}
            renderInput={params => (
              <TextField sx={{ mt: '16px', mb: '8px' }} {...params} />
            )}
          />
        </LocalizationProvider>
      )
    case 'datetime':
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label=''
            value={fieldValue}
            onChange={newValue => onDataEntry(newValue, field.name)}
            renderInput={(params: any) => (
              <TextField sx={{ mt: '16px', mb: '8px' }} {...params} />
            )}
          />
        </LocalizationProvider>
      )
    case 'time':
      return (
        <TimeField
          field={field}
          onDataEntry={onDataEntry}
          fieldValue={fieldValue}
        />
      )
    case 'textarea':
      return (
        <TextField
          id={field.name}
          name={field.name}
          key={key}
          placeholder={field.placeholder}
          label={field.helperText}
          multiline
          rows={4}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue || ''}
        />
      )
    case 'location':
      return (
        <>
          <PlaceComponent
            field={field}
            key={key}
            onDataEntry={onDataEntry}
            fieldValue={fieldValue}
          />
        </>
      )
    case 'radio':
      return (
        <RadioGroup
          aria-labelledby={`${field.name}_label`}
          defaultValue=''
          value={fieldValue}
          key={key}
          name={field.name}
          onChange={onDataEntry}>
          {field.options.map((option: any, key: any) => (
            <FormControlLabel
              key={key}
              value={option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      )
    case 'password':
      return (
        <TextField
          id={field.name}
          name={field.name}
          type='password'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue || ''}
          required={field.isRequired}
        />
      )
    case 'external':
      return <>{children ? children : null}</>
    case 'weather':
      return (
        <TextField
          id={field.name}
          name={field.name}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue || ''}
          required={field.isRequired}
        />
      )
    case 'equipment':
      return (
        <TextField
          id={field.name}
          name={field.name}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue || ''}
          required={field.isRequired}
        />
      )
    case 'materials':
      return (
        <TextField
          id={field.name}
          name={field.name}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue || ''}
          required={field.isRequired}
        />
      )
    case 'signature':
      return (
        <TextField
          id={field.name}
          name={field.name}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue || ''}
          required={field.isRequired}
          // disabled
        />
      )
    case 'multitextitem':
      return (
        <MultiTextItemField
          id={field.name}
          name={field.name}
          label={field.label}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={(newValue: any) =>
            onCompositeFieldChange(newValue, field.name)
          }
          value={fieldValue || ''}
          required={field.isRequired}
        />
      )
    case 'multitextitemplusextras':
      return (
        <MultiTextItemFieldPlusExtras
          id={field.name}
          name={field.name}
          label={field.label}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={(newValue: any) =>
            onCompositeFieldChange(newValue, field.name)
          }
          value={fieldValue || ''}
          required={field.isRequired}
        />
      )
    case 'multittextitemarray':
      return (
        <MultiTextItemFieldArray
          id={field.name}
          name={field.name}
          label={field.label}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={
            field.hasOwnProperty('fullWidth') ? field.fullWidth : false
          }
          margin='normal'
          onChange={(newValue: any) =>
            newValue !== undefined &&
            onCompositeFieldChange &&
            onCompositeFieldChange(newValue, field.name)
          }
          value={fieldValue}
          required={field.isRequired}
          lineItemFields={field.lineItemFields}
        />
      )
    case 'singleselectuser':
      return (
        <UserSelectFieldComponent
          id={field.name}
          name={field.name}
          label={field.label}
          variant='filled'
          onDataEntry={onDataEntry}
          fieldValue={fieldValue}
          required={field.isRequired}
        />
      )
    case 'multiselectuser':
      return (
        <UserMultiSelectFieldComponent
          id={field.name}
          name={field.name}
          label={field.label}
          variant='filled'
          onDataEntry={onDataEntry}
          fieldValue={fieldValue}
          required={field.isRequired}
          multi='true'
        />
      )
    default:
      return (
        <TextField
          id={field.name}
          name={field.name}
          type='text'
          key={key}
          placeholder={field.placeholder}
          helperText={field.helperText}
          variant='filled'
          fullWidth={field.hasOwnProperty('fullWidth') ? field.fullWidth : true}
          margin='normal'
          onChange={onDataEntry}
          value={fieldValue || ''}
          required={field.isRequired}
          disabled={field.disabled ? true : false}
        />
      )
  }
}
